/**
 * global-facility.service.ts
 *
 * Reads/writes the user's global Facility override by piggy-backing on the
 * existing V5 /filters/persist endpoint (backed by legacy users_filters_lists).
 *
 * Reserved namespace (no real list page uses these strings):
 *   pageUrl    = '__global_facility__'
 *   filterType = 'facilityId'
 *   viewType   = '1'
 *
 * Storage semantics inherited from FilterController::save:
 *   - POST with a non-empty filterValue (array) deletes any prior rows in this
 *     scope then inserts one row per value. Idempotent on re-save.
 *   - POST with '' just deletes — the "All Facilities" (clear) case.
 *   - GET returns { filters: { facilityId: ['5', '8'] } } (or empty object if
 *     no override is set).
 *
 * All rows are scoped to the authenticated userID + accountID by the
 * controller, so cross-account bleed is impossible at the server boundary.
 */
import { apiClient } from './apiClient';
import type { ApiResponse } from '@/types/api.types';
import type { SavedFiltersResponse } from './filters.service';

const RESERVED_PAGE_URL = '__global_facility__';
const FILTER_TYPE = 'facilityId';
const VIEW_TYPE = '1';

export async function fetchGlobalFacility(): Promise<number[]> {
  const res = await apiClient.get<ApiResponse<SavedFiltersResponse>>(
    '/filters/persist',
    { params: { pageUrl: RESERVED_PAGE_URL, viewType: VIEW_TYPE } },
  );
  const values = res.data.data?.filters?.[FILTER_TYPE] ?? [];
  if (!Array.isArray(values)) return [];
  return values
    .map((v) => Number(v))
    .filter((n) => Number.isFinite(n) && n > 0);
}

export async function saveGlobalFacility(ids: number[]): Promise<void> {
  await apiClient.post('/filters/persist', {
    filterType: FILTER_TYPE,
    // The backend inserts one row per array element; '' clears the override.
    filterValue: ids.length === 0 ? '' : ids.map((id) => String(id)),
    pageUrl: RESERVED_PAGE_URL,
    viewType: VIEW_TYPE,
  });
}
